import type React from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WarningFilled } from '@ant-design/icons';
import { BaseAlertBanner, BaseSpin, PdfViewer } from '@components';
import { generateCertificatePdf } from '@components/certificate-pdf/index.ts';
import { useLingui } from '@lingui/react/macro';
import {
    buildCertificatePreviewData,
    readCertificatePreviewDraft,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/___pages___/certificate-preview/utils.ts';
import { CERTIFICATE_PREVIEW_DRAFT_PARAM } from '@routes/sidebar/admin-center/creator/___pages___/pathways/constants.ts';
import {
    CertificatePreviewPageWrapper as Wrapper,
    CertificatePreviewViewer,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/styled.tsx';

export const CertificatePreview: React.FC = () => {
    const { t } = useLingui();
    const [searchParams] = useSearchParams();
    // Read once on mount (the hand-over is deleted from localStorage while reading).
    const [draft] = useState(() => readCertificatePreviewDraft(searchParams.get(CERTIFICATE_PREVIEW_DRAFT_PARAM)));

    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        // false after this effect is cleaned up (page closed or draft changed): then the PDF result is ignored,
        // so no state is set on an unmounted page and no blob URL is left without revoke.
        let isActive = true;
        let objectUrl: string | null = null;

        generateCertificatePdf(buildCertificatePreviewData(draft, null))
            .then((blob) => {
                if (!isActive) return;
                objectUrl = URL.createObjectURL(blob);
                setPdfUrl(objectUrl);
            })
            .catch(() => {
                if (isActive) setHasError(true);
            });

        return () => {
            isActive = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [draft]);

    return (
        <Wrapper>
            <BaseAlertBanner
                type="warning"
                title={t`Certificate Preview Mode`}
                description={t`You are viewing a sample certificate.`}
                icon={<WarningFilled />}
            />
            <CertificatePreviewViewer>
                {!!hasError && <BaseAlertBanner type="error" title={t`Error`} />}
                {!hasError && !pdfUrl && <BaseSpin size="large" />}
                {!!pdfUrl && <PdfViewer fileUrl={pdfUrl} pdfTitle="certificate-preview" />}
            </CertificatePreviewViewer>
        </Wrapper>
    );
};
